import { useToastController } from '@fluentui/react-components';
import React, { PropsWithChildren, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import AppNotification from '../Components/Common/AppNotification';

type NotificationIntent = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
    id: string;
    message: string;
    intent: NotificationIntent;
}

interface NotificationContextValue {
    dispatchNotification: (message: string, intent: NotificationIntent, primaryActionLabel?: string, primaryAction?: () => void) => void;
}

const NotificationContext = createContext<NotificationContextValue>({
    dispatchNotification: () => undefined,
});

export const useNotification = () => useContext(NotificationContext);

const NotificationProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const { dispatchToast } = useToastController();

    const dispatchNotification = useCallback((message: string, intent: NotificationIntent, primaryActionLabel?: string, primaryAction?: () => void, timeout = 8000) => {
        setNotifications((prevNotifications) => [...prevNotifications, { id: uuidv4(), message, intent }]);
        dispatchToast(<AppNotification message={message} intent={intent} primaryActionLabel={primaryActionLabel} primaryAction={primaryAction} />, { intent, timeout });
    }, []);

    const value = useMemo(() => ({ dispatchNotification }), [notifications]);

    return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export default NotificationProvider;
